'use client'

/**
 * The "Anchored to: <label>" row inside the feedback modal (Issue #13577).
 *
 * Its own file rather than inline JSX for one reason worth stating:
 * `feedback-modal.tsx` is baselined by `check:file-size-ratchet` and is the
 * single most-edited component in the feedback surface. A row that renders
 * only when an anchor exists has no business growing that file.
 *
 * The remove control is a real button with an accessible name, sized to the
 * repo's 44x44 floor. Removing the anchor is the ONLY way back to an ordinary
 * submission once an element has been picked, so "hover to reveal a small x"
 * would make an escape hatch unreachable for a keyboard or touch viewer.
 *
 * ## What "back to an ordinary submission" requires of the CALLER
 *
 * This claim is about a round trip, and this component owns only half of it.
 * `onRemove` clears the anchor; whether the modal SURVIVES that clear is the
 * caller's business, and the obvious wiring gets it wrong. In
 * `horizontal-header.tsx` the modal's `isOpen` is
 * `isFeedbackModalOpen || anchor !== null`, so when the picker opened it the
 * anchor is the only thing holding it up — passing `clearAnchor` straight
 * through unmounted the modal and discarded whatever the viewer had typed,
 * which is the opposite of "back to an ordinary submission". The caller must
 * therefore take ownership of the open state in the same handler.
 * `feedback-modal-anchor.test.tsx` pins the round trip end to end.
 *
 * `onRemove` is required rather than optional for the same reason: an absent
 * handler renders a button that looks like the escape hatch this comment
 * promises and does nothing.
 */

import { Crosshair, X } from '@/components/icons'
import type { NormalizedFeedbackAnchor } from '@/lib/feedback/anchor-payload'

/**
 * Kept as an export because unit tests import it, but the JSX below writes the
 * id as a LITERAL: `tests/scripts/golden-path-e2e-wiring.test.ts` resolves the
 * ids a tagged e2e spec asserts against a literal spelling under `src/`, and a
 * `data-testid={CONSTANT}` has none. The two cannot drift silently — the unit
 * tests that import this constant render the component and look the row up by
 * it, so a mismatch fails them immediately.
 */
export const FEEDBACK_ANCHOR_ROW_TESTID = 'feedback-anchor-row'

export function FeedbackAnchorRow({
  anchor,
  onRemove,
}: {
  anchor: NormalizedFeedbackAnchor
  onRemove: () => void
}) {
  // `label` is nullable server-side; the selector path is the only other
  // thing that names the element, and naming it badly beats naming nothing.
  const label = anchor.label ?? anchor.selectorPath

  return (
    <div
      data-testid="feedback-anchor-row"
      className="flex items-center gap-2 rounded-md border border-stroke-regular bg-surface-panel px-3 py-1"
    >
      <Crosshair
        className="w-4 h-4 shrink-0 text-text-secondary"
        aria-hidden="true"
      />
      <span className="shrink-0 text-sm text-text-secondary">Anchored to:</span>
      <span
        className="truncate text-sm font-medium text-text-primary"
        title={label}
      >
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove anchor"
        className="ml-auto inline-flex size-8 pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] shrink-0 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-fill-10 hover:text-text-secondary"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
